if (!customElements.get('product-media')) {
class ProductMedia extends HTMLElement {
  constructor() {
    super();
    this._lightbox = null;
    this._allMediaSlides = null;
    this._allThumbSlides = null;
    this._allModalOpeners = null;
    this._pendingSelection = null;
    this._appliedColor = undefined;
    this._visibleKey = '';
    this._filtering = false;
    this._readyFrame = 0;
    this._stopDrag = event => event.preventDefault();
    this._retryInit = () => { if (this.isConnected) this.init(); };

    this.selectors = {
      slider: '[data-slider]',
      mediaItem: '[data-media-id]',
      modalOpeners: '[data-modal-opener-id]',
      zoomImages: '[data-pswp-image]',
      btnXr: '[data-shopify-xr]'
    };
    this.selectedMediaIndex =
      Number(
        this.querySelector(this.selectors.slider).querySelector(
          '[data-selected]'
        )?.dataset.index
      ) || 0;

    const swiperOptions = JSON.parse(
      this.querySelector(this.selectors.slider).getAttribute(
        'data-swiper-options'
      ) || '{}'
    ) || {};

    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:section:load', event => {
        this.init();
      });
    }
    const withThumbs = this.dataset.withThumbs;

    const totalSlides = this.getMainSlides().length;
    const isSingleSlide = totalSlides <= 1;

    this.settings = {
      sliderElement: this.querySelector(this.selectors.slider),
      sliderInstance: null,
      options: {
        initialSlide: this.selectedMediaIndex,
        direction: swiperOptions.direction || 'horizontal',
        navigation: isSingleSlide
          ? false
          : {
              prevEl: this.querySelector('.swiper-button--prev'),
              nextEl: this.querySelector('.swiper-button--next')
            },

        pagination: isSingleSlide || swiperOptions.disablePagination
          ? false
          : {
              el: this.querySelector('.swiper-pagination'),
              type: 'progressbar'
            },

        spaceBetween: swiperOptions.spaceBetween !== undefined ? swiperOptions.spaceBetween : 2,
        watchOverflow: true,
        allowTouchMove: !isSingleSlide,
        watchSlidesVisibility: true,
        watchSlidesProgress: true,
        preventInteractionOnTransition: true,
        resistanceRatio: swiperOptions.resistanceRatio !== undefined ? swiperOptions.resistanceRatio : 0.85,

        autoHeight: this.dataset.autoHeight === 'true',

        on: {
          afterInit: swiper => {
            if (!this._filtering) this.activateSlideMedia(swiper);
          },

          reachEnd: swiper => {
            const isInQuickCartModal = this.closest('quick-cart-modal');
            if (isInQuickCartModal && window.innerWidth < 750) {
              swiper.allowSlideNext = false;
            }
          },

          slideChangeTransitionEnd: swiper => {
            this.setActiveModalOpener(swiper);
          },
          transitionStart: () => {
            if (!this._filtering) this.pauseMedia();
          },
          slideChange: swiper => {
            if (this._filtering) return;
            if (this.closest('quick-cart-modal') && window.innerWidth < 750 && !swiper.isEnd) {
              swiper.allowSlideNext = true;
            }
            if (window.innerWidth >= 990) swiper.el.style.marginInlineStart = '0';
            this.setActiveModalOpener(swiper);
          },
          transitionEnd: swiper => {
            if (!this._filtering) this.activateSlideMedia(swiper);
          }
        }
      }
    };

    if (withThumbs === 'true') {
      const thumbSettings = {
        swiper: {
          el: this.querySelector('.swiper-thumbs'),

          initialSlide: this.selectedMediaIndex,

          centeredSlides: true,
          centeredSlidesBounds: true,
          // slideToClickedSlide: true,
          freeMode: {
            enabled: true,
            sticky: true
          },
          direction: 'horizontal',

          slidesPerView: 6,
          spaceBetween: 2,

          threshold: 5,

          watchOverflow: true,
          watchSlidesVisibility: true,
          watchSlidesProgress: true,

          breakpoints: {
            750: {
              direction: swiperOptions.thumbsDirectionDesktop,
              spaceBetween: 5,
              slidesPerView: 5
            }
          },

          on: {
            touchEnd: function (s, e) {
              let range = 5;
              let diff = (s.touches.diff = s.isHorizontal()
                ? s.touches.currentX - s.touches.startX
                : s.touches.currentY - s.touches.startY);
              if (diff < range || diff > -range) s.allowClick = true;
            },
            afterInit: () => {
              this.querySelector('.swiper-thumbs')?.removeAttribute('style');
            }
          }
        }
      };

      this.settings.options.thumbs = thumbSettings;

      const slides = this.getMainSlides();
      const slideCount = slides.length;
      if (slideCount === 1) {
        this.settings.options.breakpoints = {
          750: {
            direction: swiperOptions.directionDesktop || swiperOptions.direction || 'horizontal'
          },
          1000: {
            slidesPerView: 0.6,
            direction: swiperOptions.directionDesktop || swiperOptions.direction || 'horizontal'
          }
        };
      } else {
        this.settings.options.breakpoints = {
          750: {
            direction: swiperOptions.directionDesktop || swiperOptions.direction || 'horizontal',
            slidesPerView: swiperOptions.slidesPerViewDesktop || 1
          },
          1000: {
            slidesPerView: swiperOptions.slidesPerViewDesktop || 1,
            direction: swiperOptions.directionDesktop || swiperOptions.direction || 'horizontal'
          },
          1350: {
            slidesPerView: swiperOptions.slidesPerViewDesktop || 1,
            direction: swiperOptions.directionDesktop || swiperOptions.direction || 'horizontal'
          },
          1920: {
            slidesPerView: swiperOptions.slidesPerViewDesktop || 1,
            direction: swiperOptions.directionDesktop || swiperOptions.direction || 'horizontal'
          }
        };
      }

      // Add the thumbs-specific event handlers
      this.settings.options.on.init = swiper => {
        setTimeout(() => {
          if (this.isConnected && !swiper.destroyed) {
            swiper.thumbs?.swiper?.el?.classList.add('swiper-thumbs--visible');
          }
        }, 10);
      };

      this.settings.options.on.slideChangeTransitionStart =
        swiper => {
          const thumbsSwiper = swiper.thumbs?.swiper;
          if (this._filtering || !thumbsSwiper || thumbsSwiper.destroyed) return;
          const activeIndex = swiper.activeIndex;
          const thumbsActiveIndex = thumbsSwiper.activeIndex;
          if (activeIndex !== thumbsActiveIndex) {
            thumbsSwiper.slideTo(activeIndex);
          }
        };
    } else {
      const slides = this.getMainSlides();
      const slideCount = slides.length;
      if (slideCount === 1) {
        this.settings.options.slidesPerView = swiperOptions.slidesPerView || 1;
        this.settings.options.breakpoints = {
          750: {
            direction: swiperOptions.directionDesktop || swiperOptions.direction || 'horizontal',
            slidesPerView: swiperOptions.slidesPerViewDesktop || 0.6
          },
          1000: {
            slidesPerView: swiperOptions.slidesPerViewDesktop || 0.6,
            direction: swiperOptions.directionDesktop || swiperOptions.direction || 'horizontal'
          }
        };
      } else {
        this.settings.options.slidesPerView = swiperOptions.slidesPerView || 1;
        this.settings.options.breakpoints = {
          750: {
            direction: swiperOptions.directionDesktop || swiperOptions.direction || 'horizontal',
            slidesPerView: swiperOptions.slidesPerViewDesktop || 1
          },
          1000: {
            slidesPerView: swiperOptions.slidesPerViewDesktop || 1,
            direction: swiperOptions.directionDesktop || swiperOptions.direction || 'horizontal'
          },
          1350: {
            slidesPerView: swiperOptions.slidesPerViewDesktop || 1.3,
            direction: swiperOptions.directionDesktop || swiperOptions.direction || 'horizontal'
          },
          1920: {
            slidesPerView: swiperOptions.slidesPerViewDesktop || 1.7,
            direction: swiperOptions.directionDesktop || swiperOptions.direction || 'horizontal'
          }
        };
      }
    }
  }

  getMainSlides() {
    const slider = this.settings?.sliderElement || this.querySelector('[data-slider]');
    const wrapper = slider?.querySelector('.swiper-wrapper');
    return Array.from(wrapper?.children || []).filter(node => node.matches('.swiper-slide[data-media-id]'));
  }

  cacheMedia() {
    if (this._allMediaSlides) return;
    this._mainWrapper = this.settings.sliderElement.querySelector('.swiper-wrapper');
    this._thumbWrapper = this.querySelector('.swiper-thumbs .swiper-wrapper');
    this._openerWrapper = this.querySelector('.product__modal-triggers');
    this._allMediaSlides = this.getMainSlides();
    this._allThumbSlides = Array.from(this._thumbWrapper?.children || []).filter(node => node.matches('[data-media-id]'));
    this._allModalOpeners = Array.from(this.querySelectorAll('[data-modal-opener-id]'));
  }

  normalizeColor(value) {
    return String(value ?? '').normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  resolveSelectionMedia(selection) {
    const slides = this._allMediaSlides || this.getMainSlides();
    if (selection?.mediaId != null) {
      const match = slides.find(slide => String(slide.dataset.mediaId) === String(selection.mediaId));
      if (match) return match.dataset.mediaId;
    }
    // Image IDs and media IDs are different Shopify identifiers.
    if (selection?.imageId != null) {
      const match = slides.find(slide => slide.dataset.imageId && String(slide.dataset.imageId) === String(selection.imageId));
      if (match) return match.dataset.mediaId;
    }
    return null;
  }

  pauseMedia(nodes) {
    const roots = nodes || [this];
    roots.forEach(root => {
      root.querySelectorAll('video').forEach(video => video.pause());
      root.querySelectorAll('.js-youtube').forEach(frame => {
        frame.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      });
      root.querySelectorAll('.js-vimeo').forEach(frame => {
        frame.contentWindow?.postMessage('{"method":"pause"}', '*');
      });
    });
  }

  playPromise(video) {
    const result = video?.play();
    if (result && typeof result.catch === 'function') result.catch(() => { /* Autoplay may be blocked. */ });
  }

  playYoutubeVideo(element) {
    element.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
  }

  playVimeoVideo(element) {
    element.contentWindow?.postMessage('{"method":"play"}', '*');
  }

  activateSlideMedia(swiper) {
    if (!swiper || swiper.destroyed || this._filtering) return;
    const active = Array.from(swiper.slides || [])[swiper.activeIndex];
    if (!active) return;
    this.setActiveModalOpener(swiper);
    active.querySelectorAll('video').forEach(video => this.playPromise(video));
    active.querySelectorAll('.js-youtube').forEach(frame => this.playYoutubeVideo(frame));
    active.querySelectorAll('.js-vimeo').forEach(frame => this.playVimeoVideo(frame));
  }

  connectedCallback() {
    this.addEventListener('dragstart', this._stopDrag);
    document.addEventListener('DOMContentLoaded', this._retryInit, { once: true });
    window.addEventListener('load', this._retryInit, { once: true });
    this.init();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this._readyFrame);
    this.removeEventListener('dragstart', this._stopDrag);
    document.removeEventListener('DOMContentLoaded', this._retryInit);
    window.removeEventListener('load', this._retryInit);
    this.pauseMedia(this._allMediaSlides || [this]);
    this._lightbox?.destroy();
    this._lightbox = null;
    const swiper = this.settings?.sliderInstance;
    const thumbs = swiper?.thumbs?.swiper;
    if (swiper && !swiper.destroyed) swiper.destroy(true, true);
    if (thumbs && !thumbs.destroyed) thumbs.destroy(true, true);
    if (this.settings) this.settings.sliderInstance = null;
    this._appliedColor = undefined;
    this._visibleKey = '';
  }

  // Called by ProductSelector. Does not change any option or cart/checkout input.
  setVariantMedia(selection = {}) {
    this._pendingSelection = selection;
    const swiper = this.settings?.sliderInstance;
    if (!swiper || swiper.destroyed) return;
    this.cacheMedia();
    const color = this.normalizeColor(selection.color);
    const colorOf = slide => this.normalizeColor(slide.dataset.mediaColor);
    const shared = slide => ['all', '*'].includes(colorOf(slide));
    const matches = color ? this._allMediaSlides.filter(slide => colorOf(slide) === color && !shared(slide)) : [];
    // Never produce an empty gallery when tagging is incomplete. A color with
    // no matching tags uses the original gallery and its assigned variant image.
    const filtering = matches.length > 0;
    const visible = filtering ? this._allMediaSlides.filter(slide => colorOf(slide) === color || shared(slide)) : this._allMediaSlides;
    if (!visible.length) return;
    const key = visible.map(slide => slide.dataset.mediaId).join(',');
    const colorChanged = this._appliedColor !== color;
    const collectionChanged = key !== this._visibleKey;
    const requestedId = this.resolveSelectionMedia(selection);
    const current = Array.from(swiper.slides || [])[swiper.activeIndex];
    let targetId;
    if (filtering) {
      // Changing only size must not reset a color's gallery to its first image.
      targetId = !colorChanged && !collectionChanged && visible.includes(current)
        ? current.dataset.mediaId
        : matches.find(slide => slide.dataset.mediaId === requestedId)?.dataset.mediaId || matches[0].dataset.mediaId;
    } else {
      targetId = visible.find(slide => slide.dataset.mediaId === requestedId)?.dataset.mediaId ||
        (visible.includes(current) ? current.dataset.mediaId : visible[0].dataset.mediaId);
    }
    const activeIndex = Math.max(0, visible.findIndex(slide => slide.dataset.mediaId === targetId));
    const moved = current?.dataset.mediaId !== targetId;
    if (collectionChanged) {
      this._lightbox?.pswp?.close();
      this.pauseMedia();
      this._filtering = true;
      try {
        const allowed = new Set(visible.map(slide => slide.dataset.mediaId));
        const order = new Map(visible.map((slide, index) => [slide.dataset.mediaId, index]));
        this._mainWrapper.replaceChildren(...visible);
        const thumbs = this._allThumbSlides.filter(slide => allowed.has(slide.dataset.mediaId));
        thumbs.sort((a, b) => order.get(a.dataset.mediaId) - order.get(b.dataset.mediaId));
        this._thumbWrapper?.replaceChildren(...thumbs);
        const openers = this._allModalOpeners.filter(opener => allowed.has(
          opener.dataset.mediaId || opener.querySelector('[data-media-id]')?.dataset.mediaId));
        this._openerWrapper?.replaceChildren(...openers);
        visible.forEach((slide, index) => { slide.dataset.index = String(index); });
        thumbs.forEach((slide, index) => { slide.dataset.index = String(index); });
        swiper.setTransition(0);
        swiper.animating = false;
        swiper.allowSlideNext = true;
        swiper.allowSlidePrev = true;
        swiper.params.allowTouchMove = visible.length > 1;
        swiper.allowTouchMove = visible.length > 1;
        const thumbSwiper = swiper.thumbs?.swiper;
        if (thumbSwiper && !thumbSwiper.destroyed) {
          thumbSwiper.setTransition(0);
          thumbSwiper.animating = false;
          thumbSwiper.allowSlideNext = true;
          thumbSwiper.allowSlidePrev = true;
          thumbSwiper.update();
        }
        swiper.update();
        swiper.slideTo(activeIndex, 0, false);
        if (thumbSwiper && !thumbSwiper.destroyed) thumbSwiper.slideTo(activeIndex, 0, false);
        swiper.thumbs?.update?.(true);
        swiper.navigation?.update?.();
        swiper.pagination?.render?.();
        swiper.pagination?.update?.();
        if (swiper.params.autoHeight) swiper.updateAutoHeight(0);
      } finally {
        this._filtering = false;
      }
    } else if (moved) {
      this.setActiveMedia(targetId);
    }
    this._visibleKey = key;
    this._appliedColor = color;
    this.dataset.mediaColorFilter = filtering ? color : '';
    if (collectionChanged) this.activateSlideMedia(swiper);
    this.setActiveModalOpener(swiper);
  }

  init() {
    if (!this.isConnected || !this.settings?.sliderElement || typeof Swiper !== 'function') return;
    if (this.settings.sliderInstance && !this.settings.sliderInstance.destroyed) return;
    this.cacheMedia();
    const [
      zoomContainerWidth,
      zoomContainerHeight,
      closeIcon,
      prevIcon,
      nextIcon
    ] = [
      this.offsetWidth,
      this.offsetHeight,
      this.querySelector('[data-close-icon]'),
      this.querySelector('[data-prev-icon]'),
      this.querySelector('[data-next-icon]')
    ];

    function isPhone() {
      return window.innerWidth < 750;
    }

    if (typeof PhotoSwipeLightbox === 'function' && typeof PhotoSwipe === 'function') {
    const photoSwipeLightboxInstance = new PhotoSwipeLightbox({
      gallery: this.settings.sliderElement,
      // Only the attached openers are included, so zoom follows the color filter.
      children: '[data-modal-opener-id] > a[data-media-id]', 
      mainClass: 'pswp--product-media-gallery',

      loop: false,
      showHideAnimationType: 'zoom',

      initialZoomLevel: zoomLevelObject => {
        if (isPhone()) {
          return zoomLevelObject.vFill;
        } else {
          return zoomLevelObject.fit;
        }
      },
      secondaryZoomLevel: zoomLevelObject => {
        if (isPhone()) {
          return zoomLevelObject.fit;
        } else {
          return 1;
        }
      },
      pswpModule: PhotoSwipe
    });
    photoSwipeLightboxInstance.addFilter(
      'uiElement',
      (element, data) => {
        if (data.name === 'close' && closeIcon) {
          element.innerHTML = closeIcon.innerHTML;
        } else if (data.name === 'arrowPrev' && prevIcon) {
          element.innerHTML = prevIcon.innerHTML;
        } else if (data.name === 'arrowNext' && nextIcon) {
          element.innerHTML = nextIcon.innerHTML;
        }
        return element;
      }
    );

    // html for video
    photoSwipeLightboxInstance.addFilter(
      'itemData',
      (itemData, index) => {
        if (itemData.type === 'html' && itemData.element) {
          return {
            html: itemData.element.dataset.pswpHtml
          };
        }
        if (itemData.element?.dataset.pswpAlt) itemData.alt = itemData.element.dataset.pswpAlt;
        return itemData;
      }
    );

    photoSwipeLightboxInstance.init();

    this._lightbox = photoSwipeLightboxInstance;
    photoSwipeLightboxInstance.on('beforeOpen', () => this.pauseMedia());
    }

    if (!this.settings.sliderElement) return;

    this.settings.sliderInstance = new Swiper(
      this.settings.sliderElement,
      this.settings.options
    );
    this.setActiveModalOpener(this.settings.sliderInstance);
    if (this._pendingSelection) this.setVariantMedia(this._pendingSelection);
    // Product-selector may be upgraded before or after this component.
    cancelAnimationFrame(this._readyFrame);
    this._readyFrame = requestAnimationFrame(() => {
      if (!this.isConnected) return;
      const root = this.closest('quick-cart-modal, .quick-cart-product-modal, [data-main-product], .js-product') || this.parentElement;
      const selector = root?.querySelector('product-selector');
      if (typeof selector?.updateMedia === 'function') selector.updateMedia();
    });
  }

  setActiveMedia(id) {
    if (id == null) return;
    const swiper = this.settings?.sliderInstance;
    if (!swiper || swiper.destroyed) {
      this._pendingSelection = { ...(this._pendingSelection || {}), mediaId: id };
      return;
    }
    const slides = Array.from(swiper.slides || []);
    const index = slides.findIndex(slide => String(slide.dataset.mediaId) === String(id));
    if (index < 0) return; // A hidden color must not re-enter the active gallery.
    if (swiper.activeIndex !== index) {
      this.pauseMedia();
      swiper.setTransition(0);
      swiper.animating = false;
      swiper.allowSlideNext = true;
      swiper.allowSlidePrev = true;
      swiper.slideTo(index, 0, false);
      const thumbs = swiper.thumbs?.swiper;
      if (thumbs && !thumbs.destroyed) thumbs.slideTo(index, 0, false);
      swiper.thumbs?.update?.(true);
      if (swiper.params.autoHeight) swiper.updateAutoHeight(0);
      this.activateSlideMedia(swiper);
    }
    this.setActiveModalOpener(swiper);
  }

  setActiveModalOpener(swiper) {
    if (this._filtering || !swiper || swiper.destroyed) return;
    const active = Array.from(swiper.slides || [])[swiper.activeIndex];
    if (!active) return;
    const id = String(active.dataset.mediaId);
    this.querySelectorAll(this.selectors.modalOpeners).forEach(opener => {
      const openerId = opener.dataset.mediaId || opener.querySelector('[data-media-id]')?.dataset.mediaId;
      opener.classList.toggle('is-active', String(openerId) === id);
    });
    if (active.dataset.mediaType === 'model') {
      const xr = this.querySelector(this.selectors.btnXr);
      if (xr) xr.dataset.shopifyModel3dId = id;
    }
  }
}

customElements.define('product-media', ProductMedia);
}
